import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

export type MingleEvent = {
  id?: string;
  location?: string;
  address: string;
  neighborhood: string;
  restaurantName: string;
  date: Date;
  paymentLink: string;
  charity: string;
  image?: string;
  closed?: boolean;
  description?: string;
  maxConfirmed?: number;
  confirmedCount?: number;
};

export function subscribeToUpcomingEvents(
  setState: (events: MingleEvent[]) => void
) {
  // Start of today, so an event still shows on the day it happens
  // rather than disappearing once its start time passes.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const q = query(
    collection(db, "events"),
    where("date", ">", startOfToday),
    orderBy("date", "asc")
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const events = snapshot.docs
      .map(
        (doc) =>
          ({
            ...doc.data(),
            id: doc.id,
            date: doc.data().date?.toDate(),
          } as MingleEvent)
      )
      // Closed events have a deactivated Stripe link, so signing up would
      // dead-end. Checked with !== true because older events predate the flag.
      .filter((event) => event.closed !== true);

    setState(events);
  });

  return unsubscribe;
}

export function subscribeToEvent(
  eventId: string,
  setState: (event: MingleEvent) => void
) {
  const unsubscribe = onSnapshot(doc(db, "events", eventId), (doc) => {
    setState({
      ...doc.data(),
      id: doc.id,
      date: doc.data()?.date?.toDate(),
    } as MingleEvent);
  });

  return unsubscribe;
}

export async function loadEvents(
  setState?: (events: MingleEvent[]) => void,
  futureOnly?: boolean
): Promise<MingleEvent[]> {
  const now = new Date();

  const q = futureOnly
    ? query(
        collection(db, "events"),
        where("date", ">", now),
        orderBy("date", "desc")
      )
    : query(collection(db, "events"), orderBy("date", "desc"));
  const querySnapshot = await getDocs(q);

  const events = querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
      } as MingleEvent)
  );
  setState?.(events);

  return events;
}

export async function loadEvent(
  id: string,
  setState?: (event: MingleEvent) => void
): Promise<MingleEvent | undefined> {
  const docRef = doc(db, "events", id);

  try {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const event = {
        ...docSnap.data(),
        id: docSnap.id,
        date: docSnap.data().date?.toDate(),
      } as MingleEvent;
      setState?.(event);
      return event;
    }
  } catch {
    console.error("couldnt fetch event");
  }
}
